package dev.lingvo.shared.screenshots.doc

import kotlinx.serialization.ExperimentalSerializationApi
import kotlinx.serialization.descriptors.PolymorphicKind
import kotlinx.serialization.descriptors.PrimitiveKind
import kotlinx.serialization.descriptors.SerialDescriptor
import kotlinx.serialization.descriptors.SerialKind
import kotlinx.serialization.descriptors.StructureKind
import kotlinx.serialization.encoding.CompositeDecoder
import kotlinx.serialization.json.JsonArray
import kotlinx.serialization.json.JsonElement
import kotlinx.serialization.json.JsonNull
import kotlinx.serialization.json.JsonObject
import kotlinx.serialization.json.JsonPrimitive

/**
 * The type check kotlinx does not make (review finding 1).
 *
 * Even with `isLenient = false`, kotlinx's decoder accepts a quoted string where it wants a number or a boolean and
 * then parses it with the platform's own `toDouble`, so `"800d"` decodes on the JVM and `"0x320"` in the browser.
 * After a successful decode this walks the input tree against the model's descriptors, iteratively, and requires
 * the JSON type the model declares at every position: numbers and booleans unquoted, strings and enum names
 * quoted, and integer fields spelled as integers (`700`, never `7e2` or `700.0`). Pure Kotlin over the parsed
 * text, so both platforms accept exactly the same documents.
 */
@OptIn(ExperimentalSerializationApi::class)
internal object StrictTypes {
    private val INTEGER = Regex("^-?(?:0|[1-9][0-9]*)$")

    private class Item(val element: JsonElement, val descriptor: SerialDescriptor, val path: String)

    /** The first position whose JSON type differs from the model's, or null. */
    fun check(root: JsonElement, descriptor: SerialDescriptor): DesignIssue? {
        val stack = ArrayList<Item>()
        stack.add(Item(root, descriptor, ""))
        while (stack.isNotEmpty()) {
            val item = stack.removeAt(stack.size - 1)
            val element = item.element
            if (element is JsonNull) continue
            var descriptorHere = item.descriptor
            while (descriptorHere.isInline) descriptorHere = descriptorHere.getElementDescriptor(0)
            val problem = when (descriptorHere.kind) {
                StructureKind.CLASS, StructureKind.OBJECT -> fields(element, descriptorHere, item.path, stack, skip = null)
                PolymorphicKind.SEALED -> sealed(element, descriptorHere, item.path, stack)
                StructureKind.LIST -> {
                    val array = element as? JsonArray
                    array?.forEachIndexed { index, value -> stack.add(Item(value, descriptorHere.getElementDescriptor(0), "${item.path}/$index")) }
                    if (array == null) "Expected a list." else null
                }
                StructureKind.MAP -> {
                    val map = element as? JsonObject
                    map?.forEach { (key, value) -> stack.add(Item(value, descriptorHere.getElementDescriptor(1), JsonPointer.child(item.path, key.take(64)))) }
                    if (map == null) "Expected an object." else null
                }
                SerialKind.ENUM, PrimitiveKind.STRING, PrimitiveKind.CHAR ->
                    if ((element as? JsonPrimitive)?.isString == true) null else "Expected a quoted string."
                PrimitiveKind.BOOLEAN -> {
                    val primitive = element as? JsonPrimitive
                    if (primitive != null && !primitive.isString && (primitive.content == "true" || primitive.content == "false")) {
                        null
                    } else {
                        "Expected true or false, unquoted."
                    }
                }
                PrimitiveKind.INT, PrimitiveKind.LONG, PrimitiveKind.SHORT, PrimitiveKind.BYTE -> {
                    val primitive = element as? JsonPrimitive
                    if (primitive != null && !primitive.isString && INTEGER.matches(primitive.content)) {
                        null
                    } else {
                        "Expected a whole number, unquoted and without a fraction or exponent."
                    }
                }
                PrimitiveKind.DOUBLE, PrimitiveKind.FLOAT ->
                    if ((element as? JsonPrimitive)?.isString == false) null else "Expected a number, unquoted."
                else -> null
            }
            if (problem != null) return DesignIssue(DesignIssueCode.MALFORMED, item.path, problem)
        }
        return null
    }

    private fun fields(
        element: JsonElement,
        descriptor: SerialDescriptor,
        path: String,
        stack: MutableList<Item>,
        skip: String?,
    ): String? {
        val obj = element as? JsonObject ?: return "Expected an object."
        for ((key, value) in obj) {
            if (key == skip) continue
            val index = descriptor.getElementIndex(key)
            if (index == CompositeDecoder.UNKNOWN_NAME) return "Unknown field '${key.take(40)}'."
            stack.add(Item(value, descriptor.getElementDescriptor(index), JsonPointer.child(path, key)))
        }
        return null
    }

    /** A sealed descriptor holds the discriminator at 0 and, at 1, one element per subclass named by serial name. */
    private fun sealed(element: JsonElement, descriptor: SerialDescriptor, path: String, stack: MutableList<Item>): String? {
        val obj = element as? JsonObject ?: return "Expected an object."
        val type = obj["type"] as? JsonPrimitive
        if (type == null || !type.isString) return "Expected a quoted \"type\"."
        val subclasses = descriptor.getElementDescriptor(1)
        val index = subclasses.getElementIndex(type.content)
        if (index == CompositeDecoder.UNKNOWN_NAME) return "Unknown type '${type.content.take(40)}'."
        return fields(obj, subclasses.getElementDescriptor(index), path, stack, skip = "type")
    }
}
